import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Inject, Input,
  OnDestroy, OnInit, Output, PLATFORM_ID, Renderer2, ViewChild, ViewEncapsulation
} from '@angular/core';

import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';

import { Observable, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import {
  BrandsEnum, BrandThemesEnum, DeviceService, displayAnimation, expandCollapseAnimation, fadeInOutAnimation,
  ImageRatioEnum, PositionEnum, SdBannerComponent, SdButtonComponent, SdHtmlComponent, SdLinkComponent,
  SdSkeletonComponent, SdTitleComponent, SkeletonAppearanceEnum, ThemesEnum
} from '@seb/web-design-system/core';

import type { IAccordionImage, IAccordionImageStatus } from './accordion-image.interface';

@Component({
  selector: 'sd-accordion-image',
  templateUrl: './accordion-image.component.html',
  animations: [
    fadeInOutAnimation({
      anchor: 'fadeInOutDelayIn',
      durationIn: 250,
      delayIn: 500,
      durationOut: 0,
      delayOut: 0
    }),
    displayAnimation({
      anchor: 'displayInOut',
      durationIn: 0,
      delayIn: 0,
      durationOut: 0,
      delayOut: 0
    }),
    fadeInOutAnimation({
      anchor: 'fadeInOutBg',
      durationIn: 250,
      delayIn: 350,
      durationOut: 500,
      delayOut: 0
    }),
    expandCollapseAnimation({
      anchor: 'expandCollapse',
      durationIn: 250,
      delayIn: 350,
      durationOut: 500,
      delayOut: 0
    })
  ],
  styleUrls: ['./accordion-image.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    SdButtonComponent,
    SdBannerComponent,
    SdTitleComponent,
    SdLinkComponent,
    SdSkeletonComponent,
    TranslateModule,
    SdHtmlComponent
  ]
})
export class SdAccordionImageComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private cdRef: ChangeDetectorRef,
    private deviceService: DeviceService,
    private renderer: Renderer2,
  ) {}

  skeletonAppearanceEnum = SkeletonAppearanceEnum;
  positionEnum = PositionEnum;
  deviceSubscription: Subscription;
  mobileSubscription: Subscription;
  isDevice$: boolean;
  isMobile$: boolean;
  themesEnum = ThemesEnum;
  eventsSubscription: Subscription;
  newData: IAccordionImage = {};
  imageRatioEnum = ImageRatioEnum;

  @Input()
  brand: BrandsEnum;

  @Input()
  isLoading = false;

  @Input()
  data: IAccordionImage;

  @Input()
  theme: ThemesEnum.dark | ThemesEnum.light | BrandThemesEnum.brand =
    ThemesEnum.dark;

  @Input()
  isSmall: boolean;

  @Input()
  events: Observable<number>;

  @Input()
  indexSlice: number;

  @Input()
  mediaIsMatchingIndex = false;

  @Input()
  slicesNumber: number;

  @Output()
  sliceClosed = new EventEmitter<IAccordionImageStatus>();

  @Output()
  sliceOpened = new EventEmitter<IAccordionImageStatus>();

  @Output()
  urlClicked = new EventEmitter<void>();

  @Output()
  imageIsLoaded = new EventEmitter<void>();

  @ViewChild('backgroundSlice')
  backgroundSlice: SdBannerComponent;

  @ViewChild('openButton', { read: ElementRef })
  openButton: ElementRef;

  @ViewChild('closeButton', { read: ElementRef })
  closeButton: ElementRef;

  @Input() class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.isSmall ? 'is-small' : '',
      this.theme ? 'is-theme-' + this.theme : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  ngOnInit() {
    this.deviceSubscription = this.deviceService.isDevice.subscribe((x) => {
      this.isDevice$ = x;
      this.cdRef.markForCheck();
    });

    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });

    if (isPlatformBrowser(this.platformId)) {
      this.eventsSubscription = this.events.subscribe((value: number) =>
        this.updateBackgroundStyles(value),
      );
    }
  }

  ngOnDestroy() {
    this.deviceSubscription?.unsubscribe();
    this.eventsSubscription?.unsubscribe();
    this.mobileSubscription?.unsubscribe();
  }

  updateBackgroundStyles(value: number) {
    const sliceMedia = this.backgroundSlice?.targetMedia?.nativeElement;
    const mediaPositionIndex = -(this.data?.sliceSize * this.indexSlice);
    const mediaPositionCentered =
      -(this.slicesNumber * 50) - this.data?.sliceSize;
    if (sliceMedia) {
      if (!this.isDevice$) {
        this.renderer.setStyle(sliceMedia, 'position', 'absolute');
        this.renderer.setStyle(sliceMedia, 'width', value + 'px');
        if (this.mediaIsMatchingIndex) {
          this.renderer.setStyle(sliceMedia, 'left', mediaPositionIndex + 'px');
        } else {
          this.renderer.setStyle(
            sliceMedia,
            'left',
            mediaPositionCentered + 'px',
          );
        }
      } else {
        this.renderer.setStyle(sliceMedia, 'width', '100%');
        this.renderer.setStyle(sliceMedia, 'left', '0');
      }
    }
    this.newData = this.data?.media;
  }

  clickToOpen(index: number) {
    this.sliceOpened.emit({ open: true, sliceIndex: index });
    /* A11Y */
    setTimeout(() => {
      this.closeButton.nativeElement.focus();
    });
  }

  clickToClose(index: number) {
    this.sliceClosed.emit({ close: true, sliceIndex: index });
    /* A11Y */
    setTimeout(() => {
      this.openButton.nativeElement.focus();
    });
  }

  clickToUrl() {
    this.urlClicked.emit();
  }

  imageLoaded() {
    this.imageIsLoaded.emit();
  }
}
